<template>
  <b-form-group :class="required ? 'required-control' : ''">
    <label class="font-weight-bold">{{ label }}</label>
    <!-- Hiển thị readonly input với icon edit khi có imeiCode từ API -->
    <div
      v-if="(isEditMode && value && !isEditingImei) || isCreateFromBill"
      class="d-flex align-items-center"
    >
      <b-form-input
        size="sm"
        :value="value"
        readonly
        class="flex-grow-1"
      ></b-form-input>
      <b-button
        variant="link"
        class="p-0 ml-2"
        @click="onEditClick"
        style="min-width: auto"
        v-if="!isCreateFromBill"
      >
        <i class="mdi mdi-pencil text-primary" style="font-size: 18px"></i>
      </b-button>
    </div>
    <!-- Hiển thị Autosuggest khi đang edit hoặc tạo mới -->
    <Autosuggest
      v-else
      :model="imeiValueForTreeselect"
      @update:model="onModelUpdate"
      :suggestions="optionImeis"
      :placeholder="placeholder"
      :limit="10"
      @change="onImeiSearchChange"
      @select="onSelectImei"
      :disabled="isCreateFromBill"
      suggestionName="imeiNo"
    >
      <template #custom="{ suggestion }">
        <div style="width: 100%;">
          <div style="display: flex; align-items: center; gap: 8px">
            <ProductImage
              :src="suggestion.item.productImageUrl"
              style="width: 40px; height: 40px; object-fit: contain"
            />
            <div>
              <div v-if="suggestion.item.productName">
                {{ suggestion.item.productName }}
              </div>
              <div class="text-primary font-italic" v-if="suggestion.item.imeiNo">
                IMEI: {{ suggestion.item.imeiNo }}
              </div>
            </div>
          </div>
          <div class="mt-2" v-if="suggestion.item.latestBillNumber">
            <div class="text-muted" style="font-size: 10px; line-height: 1.4">
              HĐ:
              <span class="text-primary">{{
                suggestion.item.latestBillNumber
              }}</span>
              <span>
                - {{ suggestion.item.latestBillCreatedAt }} -
                {{ suggestion.item.latestBillStoreName }}</span
              >
            </div>
          </div>
        </div>
      </template>
    </Autosuggest>
  </b-form-group>
</template>

<script>
import ApiService from '@/core/services/api.service';
import ProductImage from '@/view/components/products/ProductImage.vue';
import { convertToDateSlash, DATE_FORMAT } from '@/utils/date';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import debounce from 'debounce';
import {
  PRODUCT_SEARCH_DEBOUNCE_MS,
  searchByImei,
  unwrapProductSearchData,
} from '@/utils/productSearch';

export default {
  name: 'ImeiSearch',
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'IMEI/Seri',
    },
    placeholder: {
      type: String,
      default: 'Nhập IMEI/Seri để tìm kiếm',
    },
    required: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    isCreateFromBill: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ProductImage,
    Autosuggest,
  },
  data() {
    return {
      isOpenImeiSearch: false,
      isEditingImei: false,
      imeiValueForTreeselect: null,
      optionImeis: [],
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.trim() && !this.isEditingImei) {
          this.imeiValueForTreeselect = newVal.trim();
        }
      },
    },
  },
  methods: {
    onModelUpdate(val) {
      this.imeiValueForTreeselect = val;
      if (val && val.trim().length > 0) {
        // Emit input so the parent form has the text even if no option is selected
        this.$emit('input', val);
      } else {
        this.$emit('input', '');
      }
    },
    onImeiSearchChange: debounce(async function(searchQuery) {
      if (!searchQuery || searchQuery.trim().length < 3) {
        this.optionImeis = [];
        this.$emit('imei-searching', false);
        return;
      }
      this.$emit('imei-searching', true);
      try {
        const result = await this.fetchProductByImei(searchQuery.trim());
        const latestBillInfo = await this.getLatestBillInfo(searchQuery.trim());

        const optionImeis = (result.data || []).map((element) => {
          const optionImei = {
            id: element.imeiNo,
            label: element.imeiNo || '',
            ...element,
          };
          if (latestBillInfo) {
            Object.assign(optionImei, {
              latestBillNumber: latestBillInfo.billNumber,
              latestBillCreatedAt: convertToDateSlash(
                latestBillInfo.createdAt,
                DATE_FORMAT,
              ),
              latestBillCreatedAtRaw: latestBillInfo.createdAt, // Giữ nguyên format gốc để fill ngày bán
              latestBillStoreName: latestBillInfo.storeName,
            });
          }
          return optionImei;
        });

        this.optionImeis = optionImeis;
        this.$emit('imei-searching', false);
      } catch (error) {
        console.error('Error loading IMEI options:', error);
        this.optionImeis = [];
        this.$emit('imei-searching', false);
      }
    }, PRODUCT_SEARCH_DEBOUNCE_MS),
    getLatestBillInfo(imeiNo) {
      return ApiService.get('v1/bills/by-imei', {
        params: { imeiNo: imeiNo },
      }).then((response) => {
        return response.data.data;
      });
    },
    async fetchProductByImei(searchText, options = {}) {
      const response = await searchByImei({ searchText }, options);
      return { data: unwrapProductSearchData(response) };
    },
    async onEditClick() {
      // Bật chế độ edit trước
      this.isEditingImei = true;

      // Khi click edit, load option trước để tránh hiển thị "(unknown)"
      if (this.value && this.value.trim()) {
        this.imeiValueForTreeselect = this.value.trim();
        this.onImeiSearchChange(this.value.trim());
      } else {
        // Nếu không có imeiCode, clear value
        this.imeiValueForTreeselect = null;
        this.optionImeis = [];
      }
    },
    onSelectImei(node) {
      this.isOpenImeiSearch = false;
      this.isEditingImei = false;

      const product = node?.item || node?.raw || node || {};

      const imeiCode = product.imeiNo || product.imeiCode || '';

      const productName = product.productName || product.name || '';

      const productData = {
        imeiCode,
        productName: productName,
      };

      if (product.productId) {
        productData.productId = product.productId;
      }

      // Thêm thông tin bill nếu có
      if (product.latestBillCreatedAt) {
        productData.latestBillCreatedAt = product.latestBillCreatedAt;
      }
      if (product.latestBillCreatedAtRaw) {
        productData.latestBillCreatedAtRaw = product.latestBillCreatedAtRaw;
      }

      // Cập nhật value cho treeselect
      this.imeiValueForTreeselect = imeiCode;

      // Emit imei-selected event - parent component sẽ xử lý update cả imeiCode và productName
      this.$emit('imei-selected', productData);

      // Không emit input ở đây để tránh conflict
      // onImeiSelected trong parent sẽ emit input với full warranty object bao gồm cả productName
    },
  },
};
</script>

<style scoped>
.required-control :deep(.vue-treeselect__menu) label::after,
.required-control :deep(.vue-treeselect__option) label::after,
.required-control :deep(.vue-treeselect__option-label) label::after {
  content: none !important;
}
</style>
